/**
 * Conditional Logger Utility
 *
 * Wraps console methods to allow disabling logs in production.
 * Set REACT_APP_DEBUG=true in .env to enable debug logging.
 *
 * Usage:
 *   import { logger } from '../utils/logger';
 *   logger.log('message');
 *   logger.error('error');
 *   logger.warn('warning');
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isDebugEnabled = process.env.REACT_APP_DEBUG === 'true';

// Enable logging in development or when REACT_APP_DEBUG is set
const LOGGING_ENABLED = isDevelopment || isDebugEnabled;

/**
 * Logger that only outputs in development/debug mode.
 * Prevents sensitive data from appearing in production console.
 */
export const logger = {
  log: (...args) => {
    if (LOGGING_ENABLED) {
      console.log(...args);
    }
  },

  info: (...args) => {
    if (LOGGING_ENABLED) {
      console.info(...args);
    }
  },

  warn: (...args) => {
    if (LOGGING_ENABLED) {
      console.warn(...args);
    }
  },

  error: (...args) => {
    // Always log errors, but sanitize in production
    if (LOGGING_ENABLED) {
      console.error(...args);
    } else {
      // In production, log error type only, not full details
      const sanitizedArgs = args.map(arg => {
        if (arg instanceof Error) {
          return `Error: ${arg.name}`;
        }
        if (typeof arg === 'object') {
          return '[Object]';
        }
        return typeof arg;
      });
      console.error('[App Error]', ...sanitizedArgs);
    }
  },

  debug: (...args) => {
    if (LOGGING_ENABLED) {
      console.debug(...args);
    }
  },

  // Group logging for structured output
  group: (label) => {
    if (LOGGING_ENABLED) {
      console.group(label);
    }
  },

  groupEnd: () => {
    if (LOGGING_ENABLED) {
      console.groupEnd();
    }
  },

  // Table logging for arrays/objects
  table: (data) => {
    if (LOGGING_ENABLED) {
      console.table(data);
    }
  },
};

// Named export for convenience
export default logger;
